/* BLANC SPACE: phone-colour pill on collection and search pages (12-13 Oct 2026); on pages without the
   pill (homepage rails) cards silently follow the remembered colour. Contract in
   AGENTS.md -> "Phone colour on collection cards".

   The pill (third on the toolbar) and the drawer's swatch row offer the finishes
   the engine has for the collection's phone model, read ONCE from the Worker's
   finishes.json (a 5 KB file cached for a day) - never from product catalogs.
   Picking a non-default finish rewrites every engine-rendered card's image
   (both carousel slides) to the Worker's shot resolver:
     /{sku}/shot.jpg?model=&case=&bumper=&pos=&colour=&w=
   sized through `?w=` to the card's own widths, so nothing larger than the
   card ever downloads. The default finish restores the Shopify image, so a
   shopper who never picks a colour sees no change and makes no extra request.
   The choice is the same localStorage key the product page uses, so it carries
   to the product page, back to any collection and across visits. */
(() => {
  const STORE_KEY = 'blancspace.phoneColour';
  const FINISHES_URL = 'https://case-images.blancspace.workers.dev/finishes.json';
  const WORKER = 'https://case-images.blancspace.workers.dev';
  const WIDTHS = [300, 400, 500, 600, 800];
  if (window.__bsCollectionColour) return;
  window.__bsCollectionColour = true;
  // the section is replaced in place on a phone-model change or a facet update (new pill, new drawer block),
  // so every node is looked up through `ui` and re-bound by the observer below
  let shell, button, menu, list, swatch, current, drawerBlock, drawerList;
  const bindNodes = () => {
    shell = document.querySelector('[data-bs-collection-colour]');
    if (!shell) return false;
    button = shell.querySelector('[data-bs-colour-button]');
    menu = shell.querySelector('[data-bs-colour-menu]');
    list = shell.querySelector('[data-bs-phone-colours-list]');
    swatch = shell.querySelector('[data-bs-colour-swatch]');
    current = shell.querySelector('[data-bs-colour-current]');
    drawerBlock = document.querySelector('[data-bs-collection-colour-drawer]');
    drawerList = document.querySelector('[data-bs-phone-colours-drawer-list]');
    if (!button || !menu || !list) return false;
    if (!shell.dataset.bsBound) {
      shell.dataset.bsBound = '1';
      button.addEventListener('click', () => (state.open ? closeMenu() : openMenu()));
      menu.addEventListener('change', (event) => {
        if (!event.target || event.target.name !== 'bs-phone-colour') return;
        choose(event.target.value); closeMenu(); button.focus({ preventScroll: true });
      });
      if (drawerList) drawerList.addEventListener('change', (event) => {
        if (!event.target || event.target.name !== 'bs-phone-colour-drawer') return;
        choose(event.target.value);
      });
    }
    return true;
  };
  // pages without the pill (homepage rails) still follow the remembered colour; only cards are needed
  if (!document.querySelector('[data-bs-collection-colour], product-card[data-bs-cg-sku]')) return;

  const readStored = () => { try { return localStorage.getItem(STORE_KEY) || sessionStorage.getItem(STORE_KEY) || ''; } catch (e) { return ''; } };
  const store = (colour) => { try { localStorage.setItem(STORE_KEY, colour); sessionStorage.setItem(STORE_KEY, colour); } catch (e) { /* private mode */ } };

  const state = { finishes: null, model: '', colour: readStored(), open: false };
  const modelEntry = () => (state.finishes && state.finishes.models[state.model]) || null;
  const isDefault = () => { const m = modelEntry(); return !m || !state.colour || state.colour === m.default || !m.colours.includes(state.colour); };
  // per card: the remembered colour applies only where THAT card's phone model offers it (a search or
  // homepage rail can mix models; a model without the finish keeps its Shopify image)
  const colourFor = (card) => {
    const m = state.finishes && state.finishes.models[card.dataset.bsCgModel || ''];
    if (!m || !state.colour || state.colour === m.default || !m.colours.includes(state.colour)) return '';
    return state.colour;
  };

  // ---- cards -------------------------------------------------------------------------------
  const shotUrl = (card, pos, w) => {
    const q = new URLSearchParams({ model: card.dataset.bsCgModel || '', case: card.dataset.bsCgCase || '', bumper: card.dataset.bsCgBumper || '', pos: String(pos), colour: state.colour });
    return `${WORKER}/${encodeURIComponent(card.dataset.bsCgSku)}/shot.jpg?${q.toString()}${w ? `&w=${w}` : ''}`;
  };
  const restore = (img) => {
    if (img.dataset.bsOrigSrc === undefined) return;
    img.setAttribute('src', img.dataset.bsOrigSrc);
    if (img.dataset.bsOrigSrcset) img.setAttribute('srcset', img.dataset.bsOrigSrcset); else img.removeAttribute('srcset');
    delete img.dataset.bsColourSrc;
  };
  const srcsetFor = (card, pos) => WIDTHS.map((w) => `${shotUrl(card, pos, w)} ${w}w`).join(', ');
  const swap = (img, card, pos) => {
    const url = shotUrl(card, pos, 600);
    if (img.dataset.bsColourSrc === url) return;
    if (img.dataset.bsOrigSrc === undefined) {
      img.dataset.bsOrigSrc = img.getAttribute('src') || '';
      img.dataset.bsOrigSrcset = img.getAttribute('srcset') || '';
      // a Worker miss (no render for that model / an outage) keeps the Shopify image
      img.addEventListener('error', () => { if (img.dataset.bsColourSrc) restore(img); });
    }
    img.dataset.bsColourSrc = url;
    img.setAttribute('srcset', srcsetFor(card, pos));
    img.setAttribute('src', url);
  };
  // vanilla Impact rewrites a card's images on a swatch tap (data-variant-media); the shown variant's
  // model / case / bumper ride on the swatch input (option-value.liquid), so the card is re-stamped first
  const restampFromSwatch = (input) => {
    const card = input.closest('product-card');
    if (!card || !card.dataset.bsCgSku) return null;
    if (input.dataset.variantModel) card.dataset.bsCgModel = input.dataset.variantModel;
    if (input.dataset.variantCase) card.dataset.bsCgCase = input.dataset.variantCase;
    if (input.dataset.variantBumper) card.dataset.bsCgBumper = input.dataset.variantBumper;
    return card;
  };
  const applyCard = (card) => {
    const imgs = card.querySelectorAll('.product-card__image--primary, .product-card__image--secondary');
    imgs.forEach((img) => {
      const pos = img.classList.contains('product-card__image--secondary') ? 2 : 1;
      if (!card.dataset.bsCgSku || !colourFor(card)) { restore(img); return; }
      // vanilla's swatch swap replaces src/srcset: forget the stale "original" so the next restore is right
      if (img.dataset.bsColourSrc && img.getAttribute('src') !== img.dataset.bsColourSrc && !img.getAttribute('src').includes('/shot.jpg')) { delete img.dataset.bsOrigSrc; delete img.dataset.bsColourSrc; }
      swap(img, card, pos);
    });
  };
  const applyAll = (scope) => (scope || document).querySelectorAll('product-card[data-bs-cg-sku]').forEach(applyCard);

  // ---- pill + menus ------------------------------------------------------------------------
  const meta = (colour) => (state.finishes && state.finishes.colours[colour]) || { label: colour, hex: '#ccc' };
  const paintPill = () => {
    const m = modelEntry(); const colour = isDefault() ? (m ? m.default : '') : state.colour;
    if (swatch) swatch.style.setProperty('--swatch-background', meta(colour).hex);
    if (current) current.textContent = meta(colour).label;
  };
  const item = (colour, selected, name) => {
    const c = meta(colour);
    const label = document.createElement('label');
    label.className = 'bs-colour-menu__item';
    label.innerHTML =
      `<input class="sr-only" type="radio" name="${name}" value="${colour}"${colour === selected ? ' checked' : ''}>` +
      `<span class="bs-colour-menu__swatch" style="--swatch-background:${c.hex}" aria-hidden="true"></span>` +
      `<span class="bs-colour-menu__name">${c.label}</span>` +
      '<svg class="bs-colour-menu__tick" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>';
    return label;
  };
  const renderMenus = () => {
    if (!shell) return;
    const m = modelEntry();
    const colours = m ? m.colours : [];
    const selected = isDefault() ? (m ? m.default : '') : state.colour;
    list.replaceChildren(...colours.map((c) => item(c, selected, 'bs-phone-colour')));
    if (drawerList) drawerList.replaceChildren(...colours.map((c) => item(c, selected, 'bs-phone-colour-drawer')));
    const show = colours.length > 1;
    shell.hidden = !show;
    if (drawerBlock) drawerBlock.hidden = !show;
    paintPill();
  };

  const positionMenu = () => {
    const rect = button.getBoundingClientRect();
    menu.style.left = `${Math.max(8, Math.min(rect.left, window.innerWidth - menu.offsetWidth - 8))}px`;
    menu.style.top = `${rect.bottom + 8}px`;
    menu.style.bottom = '';
  };
  const closeMenu = () => { if (!state.open) return; state.open = false; menu.hidden = true; button.setAttribute('aria-expanded', 'false'); };
  const openMenu = () => {
    if (state.open) return;
    state.open = true; menu.hidden = false; positionMenu(); button.setAttribute('aria-expanded', 'true');
    const checked = menu.querySelector('input:checked') || menu.querySelector('input');
    if (checked) checked.focus({ preventScroll: true });
  };
  document.addEventListener('click', (event) => { if (state.open && shell && !shell.contains(event.target)) closeMenu(); });
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && state.open) { closeMenu(); button.focus(); } });
  // close only when the PAGE scrolls: the toolbar and the menu's own list can emit scroll events on focus,
  // which closed the menu in the same tap that opened it (iPhone Chrome, 12 Oct 2026)
  window.addEventListener('scroll', (event) => { if (event.target === document) closeMenu(); }, { capture: true, passive: true });
  window.addEventListener('resize', closeMenu);

  const choose = (colour) => {
    const previous = state.colour;
    state.colour = colour;
    store(colour);
    renderMenus();
    applyAll();
    const m = modelEntry();
    try { window.posthog?.register?.({ phone_colour_pref: colour }); } catch (e) { /* analytics is best effort */ }
    if (typeof window.bsTrack === 'function') window.bsTrack('phone_colour_selected', {
      colour, from: previous || null, is_default: !!m && colour === m.default,
      phone_model: state.model, surface: 'collection',
      collection: (location.pathname.match(/\/collections\/([^/?#]+)/) || [])[1] || null,
    });
  };

  // ---- keep up with the page --------------------------------------------------------------
  // swatch taps (after vanilla has swapped the media), the in-place grid refresh on a model change,
  // and infinite-scroll appends
  // Impact fades the old <img> out and inserts a NEW one (holding the Shopify, i.e. default-finish, photo)
  // ~150 ms after the tap; recolouring the old element was overwritten by that insert (Scott, 13 Oct:
  // "the correct phone colour loads briefly then goes to silver"). So: re-stamp the card now, preload the
  // new variant's shot so the swap is instant, and let the observer below recolour the inserted <img>
  // before it is ever painted. The late applyCard is a fallback for a tap that inserts nothing.
  document.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest('.bs-collection-card__swatches input');
    if (!input) return;
    const card = restampFromSwatch(input);
    if (!card) return;
    const primary = card.querySelector('.product-card__image--primary');
    if (colourFor(card) && primary) { const pre = new Image(); pre.sizes = primary.sizes || '50vw'; pre.srcset = srcsetFor(card, 1); }
    setTimeout(() => applyCard(card), 700);
  });
  // MutationObserver callbacks run before the next paint, so a card image inserted by Impact (swatch tap),
  // an infinite-scroll append or the in-place section replace is recoloured before anyone sees silver
  const IMG = '.product-card__image--primary, .product-card__image--secondary';
  const observer = new MutationObserver((records) => {
    let cardsAdded = false; const imgCards = new Set();
    records.forEach((r) => r.addedNodes.forEach((n) => {
      if (n.nodeType !== 1) return;
      if (n.matches('img') && n.matches(IMG)) { const c = n.closest('product-card[data-bs-cg-sku]'); if (c) imgCards.add(c); return; }
      if (n.matches('product-card') || n.querySelector('product-card')) cardsAdded = true;
    }));
    if (!cardsAdded && !imgCards.size) return;
    if (cardsAdded) {
      const fresh = document.querySelector('[data-bs-collection-colour]');
      if (fresh && fresh !== shell) { state.open = false; bindNodes(); if (shell && shell.dataset.model) state.model = shell.dataset.model; renderMenus(); }
      if (state.finishes) applyAll();
    } else if (state.finishes) imgCards.forEach(applyCard);
  });
  observer.observe(document.body, { childList: true, subtree: true });
  document.addEventListener('blancspace:phone-model-change', (event) => {
    const model = event.detail && event.detail.model;
    if (model) { state.model = model; renderMenus(); applyAll(); }
  });

  bindNodes();
  state.model = (shell && shell.dataset.model) || '';
  fetch(FINISHES_URL, { credentials: 'omit' }).then((r) => (r.ok ? r.json() : null)).then((finishes) => {
    if (!finishes) return;
    state.finishes = finishes;
    renderMenus();
    applyAll();
  }).catch(() => {});
})();
